import * as React from 'react';
import { useAtomValue, useSetAtom } from 'jotai';

import { NMIResolution } from '@origin-digital/nmi-resolution';

import {
  selectedEnergyAtom,
  nmiResolutionStateAtom,
  originAddressesAtom,
  selectedAddressAtom,
  unconfirmedAddressAtom,
} from './atoms';

import type { EnergyAddressConfiguration } from './types';

export const EnergyAddressContext = React.createContext<EnergyAddressConfiguration | undefined>(
  undefined
);

export const useEnergyAddressConfig = () => {
  const context = React.useContext(EnergyAddressContext);
  if (context === undefined) {
    throw new Error('useEnergyAddressConfig must be used within EnergyAddressLookupProvider');
  }
  return context;
};

export type EnergyAddressProviderProps = {
  config: EnergyAddressConfiguration;
  children: React.ReactNode;
};

export const EnergyAddressLookupProvider: React.FC<EnergyAddressProviderProps> = ({
  config,
  children,
}) => {
  const selectedAddress = useAtomValue(selectedAddressAtom);
  const unconfirmedAddress = useAtomValue(unconfirmedAddressAtom);
  const originAddresses = useAtomValue(originAddressesAtom);
  const selectedEnergy = useAtomValue(selectedEnergyAtom);
  const setResolutionState = useSetAtom(nmiResolutionStateAtom);

  return (
    <EnergyAddressContext.Provider value={config}>
      <NMIResolution
        address={selectedAddress || unconfirmedAddress}
        addresses={originAddresses}
        selectedFuel={selectedEnergy}
        onResolutionStateChange={(resolutionState) => setResolutionState(resolutionState)}
      >
        {children}
      </NMIResolution>
    </EnergyAddressContext.Provider>
  );
};
